import SectionHeading from "@/components/base/SectionHeading";
import { whyNow } from "@/mocks/home";

const routes = [
  {
    tag: "Conventional route",
    equation: "Fe₂O₃ + 3C → 2Fe + 3CO₂",
    text: "Coal and coke strip the oxygen from iron ore. Every tonne of steel releases carbon dioxide — making steel one of the largest industrial sources of CO₂ in the world.",
    tone: "conventional" as const,
  },
  {
    tag: "Hydrogen route",
    equation: "Fe₂O₃ + 3H₂ → 2Fe + 3H₂O",
    text: "Green hydrogen does the same job and leaves behind water vapour instead of CO₂. Same iron out, no carbon released at the reduction step.",
    tone: "hydrogen" as const,
  },
];

export default function Opportunity() {
  return (
    <section id="opportunity" className="bg-background-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="The opportunity"
          title="Steel's footprint no longer has to be."
          description="Steel is one of the largest industrial sources of CO₂ because the conventional route uses coal and coke to strip oxygen from iron ore. Hydrogen can do the same job, and it leaves behind water instead of carbon dioxide."
        />

        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {routes.map((route, index) => {
            const isHydrogen = route.tone === "hydrogen";
            return (
              <article
                key={route.tag}
                className={`reveal flex flex-col rounded-xl border p-6 md:p-8 ${
                  isHydrogen
                    ? "border-primary-200 bg-primary-50"
                    : "border-background-200 bg-background-100"
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <span
                  className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] ${
                    isHydrogen
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-200 text-foreground-600"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${isHydrogen ? "bg-primary-500" : "bg-foreground-400"}`} />
                  {route.tag}
                </span>
                <p
                  className={`mt-6 font-heading text-xl font-semibold tracking-tight md:text-2xl ${
                    isHydrogen ? "text-primary-800" : "text-foreground-800"
                  }`}
                >
                  {route.equation}
                </p>
                <p className="mt-5 text-sm leading-relaxed text-foreground-600">{route.text}</p>
              </article>
            );
          })}
        </div>

        <div className="mt-16">
          <div className="reveal flex items-center gap-3">
            <span className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-foreground-500">
              Why now
            </span>
            <span className="h-px flex-1 bg-background-200" />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {whyNow.map((item, index) => (
              <article
                key={item.title}
                className="reveal rounded-lg border border-background-200 bg-background-50 p-6"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <span className="font-heading text-3xl font-light text-background-300">{item.tag}</span>
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}